import {KeywordType} from '@/enum/keyword_type';
import type {KeywordForAi} from '@/model/Article';
import {MAX_AI_KEYWORDS} from '@/utils/Article';
import {keywordAiVolume} from '@/service/serpFeatures';
import {MAX_SELECTED_CITATIONS} from '@/service/aiWritingBrief';
import {isJunkDensityKeyword} from '@/service/articleMainKeyword';

export type LucyKeywordIntent = 'section' | 'faq' | 'weave';

export const MAX_LUCY_TOPIC_SECTIONS = 6;
export const MAX_LUCY_QUESTION_SECTIONS = 4;
export const DEFAULT_CITATION_PICKS = 3;

export function keywordPhraseKey(keyword: string): string {
    return String(keyword || '').replace(/\s+/g, ' ').trim().toLowerCase();
}

export function resolveLucyIntent(row: KeywordForAi): LucyKeywordIntent {
    if (row.intent === 'section' || row.intent === 'faq' || row.intent === 'weave') {
        return row.intent;
    }
    if (row.type === KeywordType.QUESTION) {
        return 'faq';
    }
    if (row.type === KeywordType.VARIATION || row.type === KeywordType.LONG_TAIL) {
        return 'weave';
    }
    return 'section';
}

export function countsAsLucySlot(row: KeywordForAi): boolean {
    const intent = resolveLucyIntent(row);
    return intent === 'section' || intent === 'faq';
}

export function countLucySlots(rows: KeywordForAi[] | null | undefined): number {
    return (rows || []).filter(countsAsLucySlot).length;
}

export function lucySlotsFull(rows: KeywordForAi[] | null | undefined): boolean {
    return countLucySlots(rows) >= MAX_AI_KEYWORDS;
}

export function findKeywordForAi(rows: KeywordForAi[], keyword: string): KeywordForAi | undefined {
    const key = keywordPhraseKey(keyword);
    return rows.find((row) => keywordPhraseKey(row.keyword) === key);
}

export function isKeywordSelectedForLucy(rows: KeywordForAi[], keyword: string): boolean {
    return !!findKeywordForAi(rows, keyword);
}

export function defaultLucyIntent(keywordType: number, phraseIsQuestion = false): LucyKeywordIntent {
    if (keywordType === KeywordType.QUESTION || phraseIsQuestion) {
        return 'faq';
    }
    if (keywordType === KeywordType.VARIATION || keywordType === KeywordType.LONG_TAIL) {
        return 'weave';
    }
    return 'section';
}

export function keywordTypeForLucyRow(keywordType: number, intent: LucyKeywordIntent): number {
    if (intent === 'faq') {
        return KeywordType.QUESTION;
    }
    if (intent === 'weave') {
        return keywordType === KeywordType.VARIATION || keywordType === KeywordType.LONG_TAIL
            ? keywordType
            : KeywordType.VARIATION;
    }
    return keywordType === KeywordType.QUESTION ? KeywordType.RELATED : keywordType;
}

function averageSearchVolume(row: Record<string, unknown>): number {
    const info = row.keyword_info as {monthly_searches?: Array<{search_volume?: number}>; search_volume?: number} | undefined;
    if (!info) {
        return 0;
    }
    const monthly = info.monthly_searches;
    if (Array.isArray(monthly) && monthly.length) {
        return Math.round(monthly.reduce((sum, item) => sum + (item.search_volume || 0), 0) / monthly.length);
    }
    return Number(info.search_volume) || 0;
}

function sortRelatedRows(rows: Array<Record<string, unknown>>): Array<Record<string, unknown>> {
    return rows.slice().sort((a, b) => averageSearchVolume(b) - averageSearchVolume(a));
}

function sortQuestionRows(rows: Array<Record<string, unknown>>): Array<Record<string, unknown>> {
    return rows.slice().sort((a, b) => {
        const aiDiff = (keywordAiVolume(b) || 0) - (keywordAiVolume(a) || 0);
        if (aiDiff) {
            return aiDiff;
        }
        return averageSearchVolume(b) - averageSearchVolume(a);
    });
}

export function buildDefaultKeywordsForAi(input: {
    mainKeyword: string;
    relatedRows: Array<Record<string, unknown>>;
    questionRows: Array<Record<string, unknown>>;
}): KeywordForAi[] {
    const mainKey = keywordPhraseKey(input.mainKeyword);
    const topics = sortRelatedRows(input.relatedRows)
        .filter((row) => !isJunkDensityKeyword(String(row.keyword || ''), input.mainKeyword))
        .filter((row) => keywordPhraseKey(String(row.keyword || '')) !== mainKey)
        .slice(0, MAX_LUCY_TOPIC_SECTIONS)
        .map((row) => ({
            keyword: String(row.keyword || ''),
            type: KeywordType.RELATED,
            intent: 'section' as LucyKeywordIntent,
        }));
    const questions = sortQuestionRows(input.questionRows)
        .filter((row) => !isJunkDensityKeyword(String(row.keyword || ''), input.mainKeyword))
        .slice(0, MAX_LUCY_QUESTION_SECTIONS)
        .map((row) => ({
            keyword: String(row.keyword || ''),
            type: KeywordType.QUESTION,
            intent: 'faq' as LucyKeywordIntent,
        }));
    return [...topics, ...questions];
}

export function pickDefaultCitationUrls(
    citedSources: Array<{url?: string}>,
    existing: string[] | null | undefined
): string[] {
    if (Array.isArray(existing) && existing.length > 0) {
        return existing.slice(0, MAX_SELECTED_CITATIONS);
    }
    const urls = citedSources
        .map((item) => String(item.url || '').trim())
        .filter(Boolean)
        .slice(0, DEFAULT_CITATION_PICKS);
    return urls;
}

export function canTurnOnLucySlot(
    rows: KeywordForAi[],
    intent: LucyKeywordIntent
): boolean {
    if (intent !== 'section' && intent !== 'faq') {
        return true;
    }
    return countLucySlots(rows) < MAX_AI_KEYWORDS;
}

export function toggleLucyKeyword(input: {
    rows: KeywordForAi[];
    keyword: string;
    keywordType: number;
    phraseIsQuestion?: boolean;
    on?: boolean;
    intent?: LucyKeywordIntent;
}): {rows: KeywordForAi[]; blocked?: 'limit'} {
    const current = [...input.rows];
    const existing = findKeywordForAi(current, input.keyword);
    const turningOff = input.on === false || (input.on === undefined && existing);
    if (turningOff) {
        return {
            rows: current.filter((row) => keywordPhraseKey(row.keyword) !== keywordPhraseKey(input.keyword)),
        };
    }
    const intent = input.intent || defaultLucyIntent(input.keywordType, input.phraseIsQuestion);
    if (!canTurnOnLucySlot(current, intent)) {
        return {rows: current, blocked: 'limit'};
    }
    const type = keywordTypeForLucyRow(input.keywordType, intent);
    const nextRow: KeywordForAi = {keyword: input.keyword, type, intent};
    if (existing) {
        return {
            rows: current.map((row) =>
                keywordPhraseKey(row.keyword) === keywordPhraseKey(input.keyword) ? nextRow : row
            ),
        };
    }
    return {rows: [...current, nextRow]};
}

export function setLucyIntent(input: {
    rows: KeywordForAi[];
    keyword: string;
    keywordType: number;
    intent: LucyKeywordIntent;
}): {rows: KeywordForAi[]; blocked?: 'limit'} {
    const existing = findKeywordForAi(input.rows, input.keyword);
    if (!existing) {
        return {rows: input.rows};
    }
    const oldCounts = countsAsLucySlot(existing);
    const newCounts = input.intent === 'section' || input.intent === 'faq';
    if (newCounts && !oldCounts && !canTurnOnLucySlot(input.rows, input.intent)) {
        return {rows: input.rows, blocked: 'limit'};
    }
    const type = keywordTypeForLucyRow(input.keywordType, input.intent);
    const next = input.rows.map((row) =>
        keywordPhraseKey(row.keyword) === keywordPhraseKey(input.keyword)
            ? {keyword: row.keyword, type, intent: input.intent}
            : row
    );
    return {rows: next};
}

export function writeCtaDisabledForSlots(slotCount: number, selectedKeyword: string): boolean {
    if (!selectedKeyword) {
        return true;
    }
    return slotCount === 0 || slotCount > MAX_AI_KEYWORDS;
}

export function weaveKeywordsFromSelection(rows: KeywordForAi[] | null | undefined): string[] {
    const seen = new Set<string>();
    const result: string[] = [];
    for (const row of rows || []) {
        if (resolveLucyIntent(row) !== 'weave') {
            continue;
        }
        const key = keywordPhraseKey(row.keyword);
        if (!key || seen.has(key)) {
            continue;
        }
        seen.add(key);
        result.push(row.keyword);
    }
    return result;
}
